<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';

const { uiSettings, updateUISettings } = useUISettings();
const { t: $t } = useI18n();

const I18N_BASE = 'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.REPLY_BOX_HEIGHT';
const DEFAULT_PERCENT = 30;

const heightOptions = [
  {
    value: 'default',
    label: `${I18N_BASE}.OPTIONS.DEFAULT.HEADING`,
    description: `${I18N_BASE}.OPTIONS.DEFAULT.DESCRIPTION`,
  },
  {
    value: 'custom',
    label: `${I18N_BASE}.OPTIONS.CUSTOM.HEADING`,
    description: `${I18N_BASE}.OPTIONS.CUSTOM.DESCRIPTION`,
  },
];

const clampPercent = value => {
  const numeric = Math.round(Number(value));
  if (!Number.isFinite(numeric)) return 0;
  return Math.min(100, Math.max(0, numeric));
};

const selectedMode = computed({
  get: () => uiSettings.value.reply_box_height_mode || 'default',
  set: value => {
    const payload = { reply_box_height_mode: value };
    if (
      value === 'custom' &&
      uiSettings.value.reply_box_height_percent === undefined
    ) {
      payload.reply_box_height_percent = DEFAULT_PERCENT;
    }
    updateUISettings(payload);
    useAlert($t(`${I18N_BASE}.UPDATE_SUCCESS`));
  },
});

const handleChange = value => {
  selectedMode.value = value;
};

const percentDraft = ref(
  uiSettings.value.reply_box_height_percent ?? DEFAULT_PERCENT
);
watch(
  () => uiSettings.value.reply_box_height_percent,
  value => {
    if (value !== undefined) percentDraft.value = value;
  }
);

const savePercent = () => {
  const clamped = clampPercent(percentDraft.value);
  percentDraft.value = clamped;
  if (clamped === uiSettings.value.reply_box_height_percent) return;
  updateUISettings({ reply_box_height_percent: clamped });
  useAlert($t(`${I18N_BASE}.UPDATE_SUCCESS`));
};
</script>

<template>
  <div class="flex flex-col gap-3 w-full">
    <div>
      <label class="text-n-gray-12 font-medium leading-6 text-sm">
        {{ $t(`${I18N_BASE}.TITLE`) }}
      </label>
      <p class="text-n-gray-11">
        {{ $t(`${I18N_BASE}.NOTE`) }}
      </p>
    </div>

    <div class="space-y-3 ms-5">
      <div
        v-for="option in heightOptions"
        :key="option.value"
        class="flex items-start gap-3"
      >
        <div class="flex items-center mt-1">
          <input
            :id="`reply-box-height-${option.value}`"
            type="radio"
            name="reply-box-height"
            :value="option.value"
            :checked="selectedMode === option.value"
            class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
            @change="handleChange(option.value)"
          />
        </div>
        <div class="min-w-0 flex-1">
          <label
            :for="`reply-box-height-${option.value}`"
            class="text-sm font-medium text-n-gray-12 cursor-pointer"
          >
            {{ $t(option.label) }}
          </label>
          <p class="text-sm text-n-gray-11 mt-1">
            {{ $t(option.description) }}
          </p>
          <div
            v-if="option.value === 'custom' && selectedMode === 'custom'"
            class="mt-2 flex items-center gap-2"
          >
            <input
              id="reply-box-height-percent"
              v-model="percentDraft"
              type="number"
              min="0"
              max="100"
              step="1"
              class="reset-base w-20 rounded-md outline outline-1 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand bg-n-alpha-black2 px-2 py-1 text-sm text-n-gray-12"
              :aria-label="$t(`${I18N_BASE}.PERCENT_LABEL`)"
              @blur="savePercent"
              @keydown.enter.prevent="savePercent"
            />
            <span class="text-sm text-n-gray-11">%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
input[type='radio'] {
  --tw-ring-color: #3b82f6;
}

input[type='radio']:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

input[type='radio']:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
  box-shadow:
    0 0 0 2px #ffffff,
    0 0 0 4px #3b82f6;
}
</style>
